/**
 * The `internal/cash` permission codes the governed lifecycle declares (§6.1),
 * plus the ones the instruction screens read to scope themselves.
 *
 * They are separate from `@/modules/cash/permissions` for the same reason this
 * directory is separate: that file is v1's and belongs to the immediate transfer
 * path. Merging them would make a C4 rename touch a file this packet does not
 * own.
 *
 * Every string is the one the route declares in `server/internal/cash/**`:
 *
 *  - `cash.transfer.create` — draft (POST) and edit a draft (PATCH) and cancel.
 *  - `cash.transfer.read` — list, detail, events and the approval route.
 *  - `cash.screening.clear` — POST /transfer-instructions/{id}/screening-clear.
 *  - `cash.screening.report` — POST /transfer-instructions/{id}/regulatory-report
 *    (the list is `cash.transfer.read`).
 *  - `cash.ic_instrument.read` — the instrument a cross-entity transfer names.
 *  - `bankrec.statement.read` — GET /bankrec/statement-lines, the confirm evidence.
 *
 * **`cash.transfer.release` is the act that instructs the bank**, is MFA
 * step-up guarded server-side, and is declared in conflict with
 * `cash.transfer.approve` — approving an instruction and instructing the bank
 * are two hands. Nothing in this app enforces that; the role editor and the
 * server do. The screens simply never offer an action the token cannot perform.
 */

export const CASH_C4_PERMISSIONS = {
  transferRead: 'cash.transfer.read',
  transferCreate: 'cash.transfer.create',
  transferSubmit: 'cash.transfer.submit',
  transferApprove: 'cash.transfer.approve',
  transferSign: 'cash.transfer.sign',
  transferRelease: 'cash.transfer.release',
  transferConfirm: 'cash.transfer.confirm',
  screeningClear: 'cash.screening.clear',
  /** POST /transfer-instructions/{id}/regulatory-report. */
  screeningReport: 'cash.screening.report',

  icInstrumentRead: 'cash.ic_instrument.read',
  statementRead: 'bankrec.statement.read',
  beneficiaryRead: 'treasury.beneficiary.read',
  mandateRead: 'treasury.mandate.read',
  bankAccountRead: 'cash.account.read',
  companyRead: 'org.company.read',
  journalRead: 'gl.journal.read',
} as const

export type CashC4Permission = (typeof CASH_C4_PERMISSIONS)[keyof typeof CASH_C4_PERMISSIONS]
